/* =============================================================================
 * FilterSection — shared filter primitive (KGH-795)
 * -----------------------------------------------------------------------------
 * The collapsible section shell that every filter sidebar in the portal is
 * built from. Extracted VERBATIM from FilterSidebar.tsx (the athlete-side
 * college search), which had eight hand-copied instances of it, so that the
 * college-coach sidebar consumes the same control rather than growing a ninth.
 *
 * The values are the ones the athlete side has shipped on, named as tokens in
 * tokens.css (--aps-control-*) rather than inlined as literals: the extraction
 * has to be token-driven AND pixel-identical for the existing caller.
 * ========================================================================== */
import React from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';

export interface FilterSectionProps {
  /** Section heading. Plain text in every current caller. */
  title: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  /** Draws the green "this section is filtering" dot beside the title. */
  active?: boolean;
  /** Ties the header button to its body for screen readers. */
  id?: string;
  /** Extra styles on the body wrapper — e.g. a scrolling option list. */
  bodyStyle?: React.CSSProperties;
  /** Right of the title, before the chevron (e.g. a result count). */
  headerAccessory?: React.ReactNode;
  /** The hairline + padding below the section. False on the last one. */
  divider?: boolean;
  children?: React.ReactNode;
}

/** A body of null/false/undefined renders NOTHING, not an empty padded box. */
function hasBody(children: React.ReactNode): boolean {
  return children !== null && children !== undefined && children !== false;
}

export const FilterSection: React.FC<FilterSectionProps> = ({
  title,
  expanded,
  onToggle,
  active = false,
  id,
  bodyStyle,
  headerAccessory,
  divider = true,
  children,
}) => {
  const bodyId = id ? `${id}-body` : undefined;
  return (
    <div
      style={{
        marginBottom: '16px',
        ...(divider ? { borderBottom: '1px solid var(--aps-control-divider)', paddingBottom: '12px' } : null),
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={expanded && hasBody(children) ? bodyId : undefined}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'none',
          border: 'none',
          padding: '8px 0',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500',
          color: 'var(--aps-control-label)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {title}
          {active && (
            <div
              aria-hidden="true"
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: 'var(--aps-success-strong)',
                boxShadow: '0 0 0 2px rgba(16, 185, 129, 0.2)',
              }}
            />
          )}
          {headerAccessory}
        </div>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {expanded && hasBody(children) && (
        <div id={bodyId} style={{ paddingTop: '8px', ...bodyStyle }}>
          {children}
        </div>
      )}
    </div>
  );
};

export default FilterSection;
